import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Drawer, { Field } from "@/components/Drawer";
import { StatusBadge, leadStatusMap, priorityMap } from "@/components/StatusBadge";
import { Plus, Search, Trash2, Pencil, User2 } from "lucide-react";
import { fmtDate, fmtMoney } from "@/lib/format";
import { toast } from "sonner";

const empty = {
  name: "",
  company: "",
  email: "",
  phone: "",
  source: "website",
  status: "new",
  priority: "medium",
  estimated_capacity_kw: "",
  estimated_value: "",
  location: "",
  notes: "",
  assigned_to: "",
};

export default function Leads() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [drawer, setDrawer] = useState(null); // {mode, data}

  const load = async () => {
    const [l, u] = await Promise.all([api.get("/leads"), api.get("/users")]);
    setRows(l.data);
    setUsers(u.data);
  };
  useEffect(() => {
    load();
  }, []);

  const userName = (id) => users.find((u) => u.id === id)?.name || "—";

  const filtered = rows.filter((r) => {
    if (filter && r.status !== filter) return false;
    if (!q) return true;
    const t = q.toLowerCase();
    return (
      r.name?.toLowerCase().includes(t) ||
      r.company?.toLowerCase().includes(t) ||
      r.email?.toLowerCase().includes(t) ||
      r.phone?.toLowerCase().includes(t)
    );
  });

  const save = async (data) => {
    const payload = {
      ...data,
      estimated_capacity_kw: data.estimated_capacity_kw === "" ? null : Number(data.estimated_capacity_kw),
      estimated_value: data.estimated_value === "" ? null : Number(data.estimated_value),
      email: data.email || null,
      assigned_to: data.assigned_to || null,
    };
    try {
      if (drawer.mode === "edit") {
        await api.patch(`/leads/${drawer.data.id}`, payload);
        toast.success("Lead updated");
      } else {
        await api.post("/leads", payload);
        toast.success("Lead created");
      }
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/leads/${id}`);
    toast.success("Lead deleted");
    load();
  };

  return (
    <div data-testid="leads-page">
      <PageHeader
        title="Leads"
        subtitle="Capture and qualify inbound solar enquiries."
        actions={
          <button className="btn-primary flex items-center gap-2" data-testid="add-lead-btn"
            onClick={() => setDrawer({ mode: "create", data: empty })}>
            <Plus className="w-4 h-4" /> New Lead
          </button>
        }
      />

      <div className="crm-card p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="leads-search"
            className="input-base pl-9"
            placeholder="Search by name, company, email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          data-testid="leads-status-filter"
          className="input-base max-w-[200px]"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {Object.entries(leadStatusMap).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <div className="text-xs text-slate-500 ml-auto">{filtered.length} leads</div>
      </div>

      <div className="crm-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <Th>Lead</Th>
              <Th>Contact</Th>
              <Th>Source</Th>
              <Th>Status</Th>
              <Th>Priority</Th>
              <Th className="text-right">Est. Value</Th>
              <Th>Assigned</Th>
              <Th>Created</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-slate-200 hover:bg-slate-50/50" data-testid={`lead-row-${r.id}`}>
                <Td>
                  <div className="font-medium text-slate-900">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.company || "—"} · {r.location || "—"}</div>
                </Td>
                <Td>
                  <div className="text-slate-700">{r.email || "—"}</div>
                  <div className="text-xs text-slate-500">{r.phone || "—"}</div>
                </Td>
                <Td className="capitalize text-slate-600">{r.source?.replace("_", " ")}</Td>
                <Td><StatusBadge value={r.status} map={leadStatusMap} /></Td>
                <Td><StatusBadge value={r.priority} map={priorityMap} /></Td>
                <Td className="text-right font-mono">{r.estimated_value ? fmtMoney(r.estimated_value) : "—"}</Td>
                <Td className="text-slate-600 text-xs">{userName(r.assigned_to)}</Td>
                <Td className="text-xs text-slate-500">{fmtDate(r.created_at)}</Td>
                <Td className="text-right">
                  <button className="btn-ghost !p-1.5" data-testid={`edit-lead-${r.id}`}
                    onClick={() => setDrawer({ mode: "edit", data: { ...empty, ...r } })}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button className="btn-ghost !p-1.5 text-red-600" data-testid={`delete-lead-${r.id}`}
                    onClick={() => remove(r.id)}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </Td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="p-10 text-center text-slate-500">
                  <User2 className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  No leads yet. Click &quot;New Lead&quot; to add one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {drawer && (
        <LeadDrawer
          drawer={drawer}
          onClose={() => setDrawer(null)}
          onSave={save}
          users={users}
        />
      )}
    </div>
  );
}

function LeadDrawer({ drawer, onClose, onSave, users }) {
  const [data, setData] = useState(drawer.data);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  return (
    <Drawer
      open
      onClose={onClose}
      title={drawer.mode === "edit" ? "Edit Lead" : "New Lead"}
      testid="lead-drawer"
      footer={
        <>
          <button className="btn-outline" onClick={onClose} data-testid="lead-cancel-btn">Cancel</button>
          <button className="btn-primary" data-testid="lead-save-btn" onClick={() => onSave(data)}>Save Lead</button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <Field label="Name *"><input className="input-base" value={data.name} data-testid="lead-name" onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Company"><input className="input-base" value={data.company || ""} data-testid="lead-company" onChange={(e) => set("company", e.target.value)} /></Field>
        <Field label="Email"><input className="input-base" type="email" value={data.email || ""} data-testid="lead-email" onChange={(e) => set("email", e.target.value)} /></Field>
        <Field label="Phone"><input className="input-base" value={data.phone || ""} data-testid="lead-phone" onChange={(e) => set("phone", e.target.value)} /></Field>
        <Field label="Source">
          <select className="input-base" value={data.source} data-testid="lead-source" onChange={(e) => set("source", e.target.value)}>
            {["website", "referral", "cold_call", "event", "social", "google_ads", "other"].map((k) => (
              <option key={k} value={k}>{k.replace("_", " ")}</option>
            ))}
          </select>
        </Field>
        <Field label="Status">
          <select className="input-base" value={data.status} data-testid="lead-status" onChange={(e) => set("status", e.target.value)}>
            {Object.entries(leadStatusMap).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </Field>
        <Field label="Priority">
          <select className="input-base" value={data.priority} data-testid="lead-priority" onChange={(e) => set("priority", e.target.value)}>
            {Object.entries(priorityMap).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </Field>
        <Field label="Location"><input className="input-base" value={data.location || ""} data-testid="lead-location" onChange={(e) => set("location", e.target.value)} /></Field>
        <Field label="Estimated Capacity (kW)"><input className="input-base" type="number" value={data.estimated_capacity_kw ?? ""} data-testid="lead-capacity" onChange={(e) => set("estimated_capacity_kw", e.target.value)} /></Field>
        <Field label="Estimated Value (₹)"><input className="input-base" type="number" value={data.estimated_value ?? ""} data-testid="lead-value" onChange={(e) => set("estimated_value", e.target.value)} /></Field>
        <Field label="Assigned To">
          <select className="input-base" value={data.assigned_to || ""} data-testid="lead-assigned" onChange={(e) => set("assigned_to", e.target.value)}>
            <option value="">— Unassigned —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Notes">
        <textarea className="input-base min-h-[100px]" value={data.notes || ""} data-testid="lead-notes" onChange={(e) => set("notes", e.target.value)} />
      </Field>
    </Drawer>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-left font-medium ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);
